import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';

const B = '#0B3D6E', T = '#0F6E56';
const CAT_LABELS = { medicament:'💊 Médicament', dispositif:'🩺 Dispositif', consommable:'🧤 Consommable', laboratoire:'🔬 Laboratoire', autre:'📋 Autre' };

export default function AdminStock() {
  const [search, setSearch]   = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [showLow, setShowLow] = useState(false);

  const { data: usersData } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/admin/users?role=client').then(r=>r.data)
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-stock', search, catFilter, clientFilter, showLow],
    queryFn: () => api.get('/admin/users/stock', { params:{ search:search||undefined, category:catFilter||undefined, owner:clientFilter||undefined, lowStock:showLow||undefined } }).then(r=>r.data),
    refetchInterval: 30000
  });

  const clients = usersData?.data || [];
  const products = data?.data || [];

  const totalValue = products.reduce((s,p) => s + (p.quantity * p.costPrice), 0);
  const lowStockCount = products.filter(p => p.quantity <= p.minQuantity).length;

  const inp = { border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 12px', fontSize:13, background:'#f8fafc', outline:'none', fontFamily:'inherit' };

  return (
    <div style={{ padding:'1.5rem', maxWidth:1100, margin:'0 auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:10 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:600, marginBottom:2 }}>Stock global — Tous les clients</h1>
          <div style={{ fontSize:13, color:'#64748b' }}>Vue consolidée de tous les stocks StockMed</div>
        </div>
        <button onClick={()=>refetch()} style={{ background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'8px 14px', borderRadius:8, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
          🔄 Actualiser
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:'1.5rem' }}>
        {[
          { label:'Produits total', value:products.length, icon:'📦', bg:'#E6F1FB', color:B },
          { label:'Stock faible', value:lowStockCount, icon:'⚠️', bg:'#fee2e2', color:'#dc2626' },
          { label:'Valeur totale', value:`${Math.round(totalValue).toLocaleString()} MAD`, icon:'💰', bg:'#E1F5EE', color:T },
          { label:'Clients actifs', value:clients.length, icon:'👥', bg:'#f3f0ff', color:'#7C3AED' },
        ].map(k=>(
          <div key={k.label} style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'1rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ width:36, height:36, borderRadius:9, background:k.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, marginBottom:8 }}>{k.icon}</div>
            <div style={{ fontSize:20, fontWeight:600, color:k.color }}>{k.value}</div>
            <div style={{ fontSize:12, color:'#64748b' }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:'1rem', flexWrap:'wrap', alignItems:'center' }}>
        <input type="text" placeholder="Rechercher produit..." value={search} onChange={e=>setSearch(e.target.value)} style={{ ...inp, flex:1, minWidth:160 }}/>
        <select value={catFilter} onChange={e=>setCatFilter(e.target.value)} style={inp}>
          <option value="">Toutes catégories</option>
          {Object.entries(CAT_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
        </select>
        <select value={clientFilter} onChange={e=>setClientFilter(e.target.value)} style={inp}>
          <option value="">Tous les clients</option>
          {clients.map(c=><option key={c._id} value={c._id}>{c.company||c.username}</option>)}
        </select>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', color:'#64748b', whiteSpace:'nowrap' }}>
          <input type="checkbox" checked={showLow} onChange={e=>setShowLow(e.target.checked)}/>
          Stock faible seulement
        </label>
        <span style={{ fontSize:12, color:'#94a3b8', whiteSpace:'nowrap' }}>{products.length} produit(s)</span>
      </div>

      {/* Table */}
      <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        {isLoading ? (
          <div style={{ padding:'3rem', textAlign:'center', color:'#94a3b8' }}>Chargement...</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Client','Produit','Catégorie','Stock','Seuil','Achat','Vente','Valeur','Statut'].map(h=>(
                    <th key={h} style={{ textAlign:'left', fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', padding:'10px 14px', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const isLow = p.quantity <= p.minQuantity;
                  const isOut = p.quantity === 0;
                  return (
                    <tr key={p._id} style={{ borderTop:'1px solid #f1f5f9', background:isOut?'#fff7f7':isLow?'#fffbf0':'transparent' }}>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ fontSize:12, fontWeight:600, color:B }}>{p.ownerCompany||p.ownerUsername||'—'}</div>
                        <div style={{ fontSize:10, color:'#94a3b8' }}>{p.ownerEmail}</div>
                      </td>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ fontSize:13, fontWeight:500 }}>{p.name}</div>
                        {p.supplier && <div style={{ fontSize:11, color:'#94a3b8' }}>{p.supplier}</div>}
                      </td>
                      <td style={{ padding:'10px 14px' }}><span style={{ background:'#f1f5f9', padding:'2px 8px', borderRadius:6, fontSize:11 }}>{CAT_LABELS[p.category]}</span></td>
                      <td style={{ padding:'10px 14px', fontWeight:700, color:isOut?'#dc2626':isLow?'#d97706':T, fontSize:14 }}>{p.quantity} {p.unit}</td>
                      <td style={{ padding:'10px 14px', fontSize:12, color:'#64748b' }}>{p.minQuantity} {p.unit}</td>
                      <td style={{ padding:'10px 14px', fontSize:12 }}>{p.costPrice} MAD</td>
                      <td style={{ padding:'10px 14px', fontSize:12, color:T }}>{p.sellPrice} MAD</td>
                      <td style={{ padding:'10px 14px', fontSize:12, fontWeight:500 }}>{Math.round(p.quantity*p.costPrice)} MAD</td>
                      <td style={{ padding:'10px 14px' }}>
                        {isOut ? <span style={{ background:'#fee2e2', color:'#dc2626', fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:8 }}>Épuisé</span>
                        : isLow ? <span style={{ background:'#fef3c7', color:'#d97706', fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:8 }}>⚠️ Faible</span>
                        : <span style={{ background:'#E1F5EE', color:T, fontSize:11, fontWeight:600, padding:'3px 8px', borderRadius:8 }}>OK</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {products.length===0 && <div style={{ textAlign:'center', padding:'3rem', color:'#94a3b8' }}>Aucun produit trouvé</div>}
          </div>
        )}
      </div>
    </div>
  );
}

